import React, { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { comparisonContent } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const Comparison = () => {
  const sectionRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const lastHighlightedRef = useRef(-1);
  const animationRef = useRef(null);
  
  const itemCount = comparisonContent.from.items.length;
  const itemHeight = 52;
  const visibleItems = 9;
  const centerIndex = Math.floor(visibleItems / 2);
  const speed = 0.8; // pixels per frame
  const pauseDuration = 1000; // 1 second pause

  // Calculate which item is highlighted based on offset
  const totalHeight = itemCount * itemHeight;
  const normalizedOffset = offset % totalHeight;
  const activeItemIndex = Math.floor(normalizedOffset / itemHeight) % itemCount;

  // Animation loop
  const animate = useCallback(() => {
    if (!isPaused) {
      setOffset(prev => prev + speed);
    }
    animationRef.current = requestAnimationFrame(animate);
  }, [isPaused]);

  // Start animation loop
  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  // Check if we've reached a new highlighted item and pause
  useEffect(() => {
    // Check if we've crossed into a new item
    const currentItem = Math.floor(normalizedOffset / itemHeight);
    const positionInItem = normalizedOffset % itemHeight;
    
    // Trigger pause when we're very close to the start of a new item (within 2 pixels)
    if (currentItem !== lastHighlightedRef.current && positionInItem < 3) {
      lastHighlightedRef.current = currentItem;
      setIsPaused(true);
      
      // Resume after pause duration
      setTimeout(() => {
        setIsPaused(false);
      }, pauseDuration);
    }
  }, [normalizedOffset, itemHeight]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.comparison-animate',
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Render items
  const renderItems = (items, isRight) => {
    const result = [];
    const totalItemsNeeded = visibleItems + 4;
    
    for (let i = -2; i < totalItemsNeeded; i++) {
      const itemIndex = ((activeItemIndex + i) % itemCount + itemCount) % itemCount;
      const item = items[itemIndex];
      const isCenter = i === centerIndex;
      
      // Calculate y position
      const itemProgress = (normalizedOffset % itemHeight) / itemHeight;
      const yOffset = -itemProgress * itemHeight;
      
      result.push(
        <div
          key={`${i}-${itemIndex}`}
          className="absolute w-full flex items-center"
          style={{
            height: `${itemHeight}px`,
            top: `${(i * itemHeight) + yOffset}px`,
          }}
        >
          <span
            className={`font-serif text-lg lg:text-xl xl:text-2xl w-full transition-opacity duration-300 ${
              isRight ? 'text-left pl-4' : 'text-right pr-4'
            } ${
              isCenter
                ? isRight ? 'text-[#4ECDC4] opacity-100' : 'text-white opacity-100'
                : isRight ? 'text-[#4ECDC4]/20' : 'text-white/20'
            }`}
          >
            {item}
          </span>
        </div>
      );
    }
    
    return result;
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 lg:py-24 bg-[#070D19] overflow-hidden comparison-animate"
    >
      <div className="grid grid-cols-2">
        {/* From Column */}
        <div className="flex flex-col items-end pr-4 lg:pr-12 xl:pr-20">
          <h3 className="font-serif text-lg lg:text-2xl xl:text-3xl text-white/40 mb-6 lg:mb-10 pr-4">
            {comparisonContent.from.label}
          </h3>
          
          <div 
            className="relative w-full overflow-hidden"
            style={{ height: `${itemHeight * visibleItems}px` }}
          >
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#070D19] via-[#070D19]/80 to-transparent z-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#070D19] via-[#070D19]/80 to-transparent z-10 pointer-events-none" />
            
            <div className="relative h-full">
              {renderItems(comparisonContent.from.items, false)}
            </div>
          </div>
        </div>

        {/* To Column */}
        <div className="flex flex-col items-start pl-4 lg:pl-12 xl:pl-20 border-l border-white/10">
          <h3 className="font-serif text-lg lg:text-2xl xl:text-3xl text-[#4ECDC4] mb-6 lg:mb-10 pl-4">
            {comparisonContent.to.label}
          </h3>
          
          <div 
            className="relative w-full overflow-hidden"
            style={{ height: `${itemHeight * visibleItems}px` }}
          >
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#070D19] via-[#070D19]/80 to-transparent z-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#070D19] via-[#070D19]/80 to-transparent z-10 pointer-events-none" />
            
            <div className="relative h-full">
              {renderItems(comparisonContent.to.items, true)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
